import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/react';
import { NotebookPen } from 'lucide-react';
import { useRightPanel } from '../../contexts/RightPanelContext';
import { DossierEditor } from '../editor/DossierEditor';
import {
  listScratchpads, createScratchpad, updateScratchpad, parseContent, type Scratchpad,
} from '../../lib/scratchpads';
import './scratchpad-drawer.css';

const AUTOSAVE_MS = 600;

// #298: the drawer can be dragged wider or narrower. No stored width means the
// original default (48% of the window, at most 620px).
export const PAD_WIDTH_KEY = 'dossier.scratchpad.width';
export const MIN_PAD_WIDTH = 320;
/** Room always left to the left of the drawer, so it never covers the page entirely. */
export const PAGE_GUTTER = 240;
const KEY_STEP = 32;

function readStoredWidth(): number | null {
  try {
    const v = Number(window.localStorage.getItem(PAD_WIDTH_KEY));
    return Number.isFinite(v) && v >= MIN_PAD_WIDTH ? Math.round(v) : null;
  } catch { return null; }
}

function storeWidth(w: number | null): void {
  try {
    if (w === null) window.localStorage.removeItem(PAD_WIDTH_KEY);
    else window.localStorage.setItem(PAD_WIDTH_KEY, String(w));
  } catch { /* storage unavailable: the width just isn't remembered */ }
}

/** Keep a width usable: at least MIN_PAD_WIDTH, and leave PAGE_GUTTER of page beside it. */
export function clampPadWidth(w: number, rightEdge: number): number {
  if (!Number.isFinite(w)) return MIN_PAD_WIDTH;
  const max = Math.max(MIN_PAD_WIDTH, rightEdge - PAGE_GUTTER);
  return Math.round(Math.min(Math.max(w, MIN_PAD_WIDTH), max));
}

export function ScratchpadDrawer() {
  const { isRegistered, isOpen: panelOpen } = useRightPanel();
  const [open, setOpen] = useState(false);
  const [pad, setPad] = useState<Scratchpad | null>(null);
  const [width, setWidth] = useState<number | null>(() => readStoredWidth());
  const [resizing, setResizing] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);

  // Ported verbatim from ScratchpadPage.tsx's autosave (final review: the
  // two files diverging - no debounce here, one PATCH per keystroke - is
  // exactly what caused this finding; do not reinvent this).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingSave = useRef<{ id: string; content: string } | undefined>(undefined);

  // The one number the whole geometry hangs off.
  const rpWidth = isRegistered && panelOpen ? 'var(--color-right-panel-width)' : '0px';

  useEffect(() => {
    let cancelled = false;
    listScratchpads()
      .then(async (pads) => {
        if (cancelled) return;
        setPad(pads.length > 0 ? pads[0] : await createScratchpad({}));
      })
      .catch(() => { /* the tab still works; the pad loads on next open */ });
    return () => { cancelled = true; };
  }, []);

  const flushSave = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = undefined; }
    const p = pendingSave.current;
    pendingSave.current = undefined;
    if (!p) return;
    updateScratchpad(p.id, { content: p.content })
      .then(() => {
        setPad((prev) => (prev && prev.id === p.id ? { ...prev, content: p.content } : prev));
      })
      .catch(() => { /* autosave retries on next edit */ });
  }, []);

  useEffect(() => () => { flushSave(); }, [flushSave]);

  const handleChange = (json: JSONContent) => {
    if (!pad) return;
    pendingSave.current = { id: pad.id, content: JSON.stringify(json) };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, AUTOSAVE_MS);
  };

  // The drawer's right edge is where the right panel begins; width runs left from it.
  const rightEdge = () => drawerRef.current?.getBoundingClientRect().right || window.innerWidth;
  const currentWidth = () => width ?? Math.round(drawerRef.current?.getBoundingClientRect().width || MIN_PAD_WIDTH);
  const commit = (w: number | null) => { setWidth(w); storeWidth(w); };

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const edge = rightEdge();
    const handle = e.currentTarget;
    handle.setPointerCapture?.(e.pointerId);
    setResizing(true);
    let last = currentWidth();
    const move = (ev: PointerEvent) => { last = clampPadWidth(edge - ev.clientX, edge); setWidth(last); };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      setResizing(false);
      storeWidth(last);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  const onHandleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Home') { e.preventDefault(); commit(null); return; }
    const delta = e.key === 'ArrowLeft' ? KEY_STEP : e.key === 'ArrowRight' ? -KEY_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    commit(clampPadWidth(currentWidth() + delta, rightEdge()));
  };

  // A custom width lifts the 620px cap; the default keeps the original 48% / 620px.
  const sizeVars = width === null ? {} : { ['--pad-w' as string]: `${width}px`, ['--pad-max' as string]: 'none', ['--pad-tab-max' as string]: '100vw' };

  return (
    <>
      <aside
        ref={drawerRef}
        className="scratchpad-drawer"
        data-testid="scratchpad-drawer"
        data-open={open ? 'true' : 'false'}
        data-resizing={resizing ? 'true' : undefined}
        style={{ ['--rp-w' as string]: rpWidth, ...sizeVars }}
        aria-hidden={!open}
        inert={!open}
      >
        <div
          className="scratchpad-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize scratchpad"
          aria-valuenow={width ?? undefined}
          aria-valuemin={MIN_PAD_WIDTH}
          title="Drag to resize - double-click to reset"
          tabIndex={open ? 0 : -1}
          onPointerDown={startDrag}
          onDoubleClick={() => commit(null)}
          onKeyDown={onHandleKey}
        />
        {pad && (
          <>
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="text-sm font-semibold text-text-primary">{pad.title}</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <DossierEditor
                content={parseContent(pad.content)}
                onUpdate={handleChange} scratchpadId={pad.id}
                excerptHost={{ type: 'scratchpad', id: pad.id }}
              />
            </div>
          </>
        )}
      </aside>

      <button
        className="scratchpad-tab"
        data-resizing={resizing ? 'true' : undefined}
        style={{ ['--rp-w' as string]: rpWidth, ...sizeVars }}
        aria-expanded={open}
        aria-label="Scratchpad"
        onClick={() => setOpen((o) => !o)}
      >
        <NotebookPen size={13} />
      </button>
    </>
  );
}
